import { LocalizationManager } from '@lib/localization';
import { type Dispatch } from '@reduxjs/toolkit';
import { showSnackbar } from './reducers/snackbarSlice';

export interface ServerError {
  error: {
    status: number;
    data: {
      message: string;
      errors: unknown;
    };
  };
}

interface ValidationErrorItem {
  path: string;
  message: string;
}

export const handleApiResult = (successMessage?: string, { handle401Error } = { handle401Error: false }) =>
  async (_: unknown,
    { dispatch, queryFulfilled }: { dispatch: Dispatch; queryFulfilled: Promise<{ data: unknown }> }) => {
    try {
      await queryFulfilled;
      if (successMessage != null) {
        dispatch(showSnackbar({ severity: 'success', message: LocalizationManager.t(successMessage) }));
      }
    } catch (error) {
      if (isServerError(error) && (handle401Error || error.error.status != 401)) {
        let message = error.error.data.message;

        if (isDataHasMultiError(error.error.data.errors)) {
          const errorMessages = error.error.data.errors
            .map(error => `${error.path}: ${error.message}`)
            .join('\n');

          message = message.concat('\n', errorMessages);
        }

        dispatch(showSnackbar({ severity: 'error', message }));
      } else {
        console.error('api.error:', error);
      }
    }
  };

const isObjectWithKey = <T>(obj: unknown, key: string): obj is Record<string, T> => {
  return typeof obj === 'object' && obj !== null && key in obj;
};

export const isServerError = (error: unknown): error is ServerError => {
  return (
    isObjectWithKey(error, 'error') &&
    isObjectWithKey(error.error, 'data') &&
    isObjectWithKey(error.error.data, 'message') &&
    typeof error.error.status === 'number' &&
    typeof error.error.data.message === 'string'
  );
};

const isValidationErrorItem = (error: unknown): error is ValidationErrorItem => {
  return (
    isObjectWithKey<string>(error, 'path') &&
    isObjectWithKey<string>(error, 'message')
  );
};

const isDataHasMultiError = (errors: unknown): errors is ValidationErrorItem[] => {
  return (
    Array.isArray(errors) &&
    errors.length > 0 &&
    errors.every(isValidationErrorItem)
  );
};
